/**
 * P5 offline save queue — the drain driver, mounted once at the app root.
 *
 * Renders nothing; lives across the whole signed-in native session (beside
 * HealthSyncMount / CoachFab). Flushes any queued run saves whenever the
 * app is likely back online: on mount, on every foreground, and on the
 * browser 'online' event. Native + signed-in only — the queue exists only
 * in the shell (tracking is shell-only), and a drain needs the user's JWT.
 * All best-effort/no-throw (drainQueue swallows every failure).
 */

import { useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { isNativeApp } from '../../lib/platform';
import { drainQueue } from '../../lib/run/saveQueueStore';

export function RunSaveQueueMount(): null {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();

  useEffect(() => {
    if (!isNativeApp() || !user) return;
    let alive = true;
    const drain = () => {
      // B56 — scoped to the account `fetchAuthed` is bound to. Another
      // account's queued run stays on the device for its own owner to drain.
      if (alive) void drainQueue(fetchAuthed, user.id);
    };

    drain(); // app open = the first flush
    window.addEventListener('online', drain);

    // Warm foregrounds via @capacitor/app's appStateChange.
    let remove: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) drain();
        });
        remove = () => void handle.remove();
      } catch {
        /* @capacitor/app unavailable — the mount-time drain still ran. */
      }
    })();

    return () => {
      alive = false;
      window.removeEventListener('online', drain);
      remove?.();
    };
  }, [user, fetchAuthed]);

  return null;
}
